import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone must be 10 digits")
    .required("Phone is required"),
  dob: Yup.date()
    .required("Date of Birth is required")
    .max(new Date(), "DOB cannot be in the future"),
  state: Yup.string().required("Please select your state"),
});

const Form = ({ closePopup }) => {
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      dob: "",
      state: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const response = await axios.post(
          "https://uttarakhanduniversities.in/generic-form.php",
          JSON.stringify(values),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          toast.success(response.data.message);

          localStorage.setItem("formSubmitted", "true");

          if (typeof window.gtag !== "undefined") {
            window.gtag("event", "conversion", {
              send_to: "AW-17078166045/a_rbCIDDvMcaEJ3EwM8_",
            });
          }

          if (typeof closePopup === "function") {
            closePopup();
          }

          navigate("/thankyou");
        } else {
          toast.error(response.data.message || "Submission failed.");
        }
      } catch (error) {
        toast.error("Something went wrong. Try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="bg-light p-4 rounded" style={{ borderRadius: "40%" }}>
      <h4 className="text-purple fw-bold text-center mb-3">ENQUIRE NOW</h4>
      <form onSubmit={formik.handleSubmit}>
        {/* Name */}
        <div className="mb-3">
          <label htmlFor="name" className="form-label text-dark">
            Name
          </label>
          <input
            type="text"
            name="name"
            id="name"
            placeholder="Name"
            className="form-control"
            {...formik.getFieldProps("name")}
          />
          {formik.touched.name && formik.errors.name && (
            <small className="text-danger">{formik.errors.name}</small>
          )}
        </div>

        {/* Email */}
        <div className="mb-3">
          <label htmlFor="email" className="form-label text-dark">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            placeholder="Email"
            className="form-control"
            {...formik.getFieldProps("email")}
          />
          {formik.touched.email && formik.errors.email && (
            <small className="text-danger">{formik.errors.email}</small>
          )}
        </div>

        {/* Phone */}
        <div className="mb-3">
          <label htmlFor="phone" className="form-label text-dark">
            Phone
          </label>
          <input
            type="tel"
            name="phone"
            id="phone"
            placeholder="Phone"
            className="form-control"
            {...formik.getFieldProps("phone")}
          />
          {formik.touched.phone && formik.errors.phone && (
            <small className="text-danger">{formik.errors.phone}</small>
          )}
        </div>

        {/* DOB */}
        <div className="mb-3">
          <label htmlFor="dob" className="form-label text-dark">
            Date of Birth
          </label>
          <input
            type="date"
            name="dob"
            id="dob"
            className="form-control"
            {...formik.getFieldProps("dob")}
          />
          {formik.touched.dob && formik.errors.dob && (
            <small className="text-danger">{formik.errors.dob}</small>
          )}
        </div>

        {/* State */}
        <div className="mb-3">
          <label htmlFor="state" className="form-label text-dark">
            State
          </label>
          <select
            name="state"
            id="state"
            className="form-control"
            {...formik.getFieldProps("state")}
          >
            <option value="">Select State</option>
            {/* All state options */}
            <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
            <option value="Assam">Assam</option>
            <option value="Bihar">Bihar</option>
            <option value="Chandigarh">Chandigarh</option>
            <option value="Chhattisgarh">Chhattisgarh</option>
            <option value="Dadra and Nagar Haveli">Dadra and Nagar Haveli</option>
            <option value="Daman and Diu">Daman and Diu</option>
            <option value="Delhi">Delhi</option>
            <option value="Goa">Goa</option>
            <option value="Gujarat">Gujarat</option>
            <option value="Haryana">Haryana</option>
            <option value="Himachal Pradesh">Himachal Pradesh</option>
            <option value="Jammu and Kashmir">Jammu and Kashmir</option>
            <option value="Jharkhand">Jharkhand</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Kerala">Kerala</option>
            <option value="Lakshadweep">Lakshadweep</option>
            <option value="Madhya Pradesh">Madhya Pradesh</option>
            <option value="Maharashtra">Maharashtra</option>
            <option value="Manipur">Manipur</option>
            <option value="Meghalaya">Meghalaya</option>
            <option value="Mizoram">Mizoram</option>
            <option value="Nagaland">Nagaland</option>
            <option value="Odisha">Odisha</option>
            <option value="Puducherry">Puducherry</option>
            <option value="Punjab">Punjab</option>
            <option value="Rajasthan">Rajasthan</option>
            <option value="Sikkim">Sikkim</option>
            <option value="Tamil Nadu">Tamil Nadu</option>
            <option value="Telangana">Telangana</option>
            <option value="Tripura">Tripura</option>
            <option value="Uttar Pradesh">Uttar Pradesh</option>
            <option value="Uttarakhand">Uttarakhand</option>
            <option value="West Bengal">West Bengal</option>
          </select>
          {formik.touched.state && formik.errors.state && (
            <small className="text-danger">{formik.errors.state}</small>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="btn btn-purple rounded-pill w-50"
          disabled={formik.isSubmitting}
        >
          Submit
        </button>
      </form>
    </div>
  );
};

export default Form;
